import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

// ─── Step definitions ────────────────────────────────────────────────────────
const STEPS = [
  {
    selector: '[data-tour="pathways"]',
    title: "Pathways",
    description:
      `This section opens the building interface where you will define your pathway. \n
       Here, you will choose a name for your pathway, set socioeconomic and demographic variable projections, define food systems and consumption patterns, configure trade and productivity variables, and set land use and environment policies and levers.\n
       After all scenarios are selected, you can submit your pathway to run the scenario combination using the scientific engine and prepare the results.`,
  },
  {
    selector: '[data-tour="scenarios"]',
    title: "Scenarios",
    description:
      `This area is dedicated to building and customizing the core drivers of your pathway analysis, allowing you to move beyond standard assumptions by building pathways that are relevant, ambitious, and aligned with your policy or research goals.\n
       Here, you can choose which major policy area you want to customize even further (Diet, Imports, Exports, Protected Areas Expansion, or Afforestation). Each has its own tab for focused scenario design. \n
       You can also decide how quickly your scenario changes are applied, allowing you to model both rapid and phased transitions. \n
       Once created, new scenarios can be selected in the Pathways section.`
  },
  {
    selector: '[data-tour="results"]',
    title: "Results",
    description:
      `In this section, you can navigate to explore charts, tables, and summary indicators that show the impacts of your pathway on food systems, land use, climate, and more.\n
       By following these steps, you can build, customize, and analyze a complete pathway in PPL, exploring the consequences of different policy and development choices for your country or region.`
  },
  {
    selector: '[data-tour="comparison"]',
    title: "Comparison",
    description:
      `This tab in PPL is designed to help you directly compare the results of multiple pathways side by side. \n
       Here, you can choose two or more pathways you have created and compare their outcomes, allowing you to see how different combinations of policy choices or assumptions affect key results.\n
       The comparison tool helps you communicate findings to stakeholders, justify policy recommendations, and make informed choices about which pathway best aligns with your objectives.`,
  },
  {
    selector: '[data-tour="ai-assistant"]',
    title: "AI Assistant",
    description:
      `This is your dedicated support area within the PPL platform. Here’s what you can do in this section: \n
        * ask questions in real time,\n
        * get guidance on scenario building,\n
        * interpret results and indicators,\n
        * receive policy and modeling insights, and\n
        * access platform support if you encounter technical issues or need help with platform features\n
       In summary, the PPL Assistant section is designed to make your experience smoother, more informative, and more interactive—empowering you to build, analyze, and communicate scenario pathways with confidence, regardless of your technical background.`
  },
];

const SPOTLIGHT_PAD = 8;
const SIDEBAR_W = 302;
const CARD_W = 500;
const CARD_H_EST = 380; // estimated height for vertical centering

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const DISCLAIMER_KEY = "ppl_disclaimer_shown";

export function useTutorial(user) {
  const [show, setShow] = useState(false);
  const [skipDisclaimer, setSkipDisclaimer] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    if (sessionStorage.getItem(DISCLAIMER_KEY)) return; // already shown after last login

    const id = setTimeout(() => {
      sessionStorage.setItem(DISCLAIMER_KEY, "1");
      setShow(true);
    }, 900);
    return () => clearTimeout(id);
  }, [user?.id]);

  // Manual open (from "Navigation Tour" button) — skip disclaimer
  const open = () => {
    setSkipDisclaimer(true);
    setShow(true);
  };

  const close = () => setShow(false);

  return { show, skipDisclaimer, open, close };
}

// ─── Description renderer (handles \n line breaks and * bullets) ─────────────
function renderDescription(text) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const elements = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    elements.push(
      <ul key={elements.length} className="list-disc list-inside space-y-0.5">
        {bulletBuffer.map((b, i) => (
          <li key={i} className="text-[13px] text-slate-600 leading-relaxed">
            {b}
          </li>
        ))}
      </ul>
    );
    bulletBuffer = [];
  };

  lines.forEach((line, i) => {
    if (line.startsWith("*")) {
      bulletBuffer.push(line.replace(/^\*\s*/, ""));
    } else {
      flushBullets();
      elements.push(
        <p key={i} className="text-[13px] text-slate-600 leading-relaxed">
          {line}
        </p>
      );
    }
  });
  flushBullets();
  return elements;
}

// ─── Overlay ─────────────────────────────────────────────────────────────────
export default function TutorialOverlay({ skipDisclaimer = false, onClose }) {
  // phase: "disclaimer" → shown only on auto-open; "tour" → spotlight walkthrough
  const [phase, setPhase] = useState(skipDisclaimer ? "tour" : "disclaimer");
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  const measure = useCallback(() => {
    if (phase !== "tour") return;
    const el = document.querySelector(current.selector);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [current?.selector, phase]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const finish = useCallback(() => {
    onClose();
  }, [onClose]);

  const next = () => (isLast ? finish() : setStep((s) => s + 1));
  const prev = () => setStep((s) => s - 1);

  // Card position: right of sidebar, vertically near the highlight
  const cardTop = (() => {
    if (!rect) return "50%";
    const vh = window.innerHeight;
    const ideal = rect.top + rect.height / 2 - CARD_H_EST / 2;
    return Math.max(16, Math.min(ideal, vh - CARD_H_EST - 16));
  })();

  const cardTransform = rect ? "none" : "translateY(-50%)";

  // ── Disclaimer phase ──────────────────────────────────────────────────────
  if (phase === "disclaimer") {
    return createPortal(
      <div
        className="fixed inset-x-0 bottom-0 z-[9999] pointer-events-none"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        <div
          className="pointer-events-auto w-full rounded-none border-x-0 border-b-0 border-t border-slate-100 bg-white shadow-[0_-14px_40px_rgba(15,23,42,0.18)] p-5 sm:p-6 flex flex-col gap-4 max-h-[42vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(30,91,79,0.10)" }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#1E5B4F" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z" />
              </svg>
            </div>
            <div>
              <p className="text-[11px] tracking-[0.15em] uppercase text-emerald-700 font-semibold">
                Public Policy Lab · Ethiopia
              </p>
              <h2 className="text-[18px] font-semibold text-amber-700 leading-tight">
                <span>⚠️</span> Disclaimer
              </h2>
            </div>
          </div>

          {/* Disclaimer text */}
          <div className="flex flex-col gap-3 text-[13.5px] text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
            <p>
              The Public Policy Lab (PPL) platform is designed to support exploratory analysis, scenario
              development, and evidence-informed policy discussions. The results generated through the platform
              are based on modeling assumptions, selected inputs, and simulated pathways, and therefore{" "}
              <strong className="text-slate-700">should not be interpreted as predictions, official statistics,
              or representations of future real-world outcomes.</strong>
            </p>
            <p>
              While CIAT and IIASA strive to ensure the technical robustness of the platform and its underlying
              models, no warranty is made regarding the accuracy, completeness, or suitability of the results
              for specific decision-making purposes. Users acknowledge that model outputs are inherently subject
              to uncertainty and remain solely responsible for the interpretation and use of the information
              generated by the platform.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={() => setPhase("tour")}
                className="px-4 py-2 text-sm font-medium text-slate-700 rounded-xl border border-black hover:bg-slate-50 transition-all duration-200"
              >
                Start tour
              </button>
              <button
                onClick={finish}
                className="px-6 py-2.5 text-sm font-semibold rounded-xl border border-[#C49A1F]/65 bg-[#1E5B4F]/90 text-white shadow-[0_6px_20px_rgba(30,91,79,0.30)] hover:bg-[#1E5B4F] transition-all duration-200"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // ── Tour phase ────────────────────────────────────────────────────────────
  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
      onClick={finish}
    >
      {/* ── Spotlight overlay ── */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id="tour-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - SPOTLIGHT_PAD}
                y={rect.top - SPOTLIGHT_PAD}
                width={rect.width + SPOTLIGHT_PAD * 2}
                height={rect.height + SPOTLIGHT_PAD * 2}
                rx={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(15,23,42,0.72)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {/* ── Gold highlight ring ── */}
      {rect && (
        <div
          className="absolute pointer-events-none rounded-xl transition-all duration-300"
          style={{
            left: rect.left - SPOTLIGHT_PAD,
            top: rect.top - SPOTLIGHT_PAD,
            width: rect.width + SPOTLIGHT_PAD * 2,
            height: rect.height + SPOTLIGHT_PAD * 2,
            boxShadow:
              "0 0 0 2.5px #C49A1F, 0 0 28px 4px rgba(196,154,31,0.40)",
          }}
        />
      )}

      {/* ── Tooltip card ── */}
      <div
        className="absolute pointer-events-auto transition-all duration-300"
        style={{
          left: SIDEBAR_W + 20,
          top: cardTop,
          transform: cardTransform,
          width: CARD_W,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Arrow toward the sidebar */}
        {rect && (
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full"
            style={{
              width: 0,
              height: 0,
              borderTop: "9px solid transparent",
              borderBottom: "9px solid transparent",
              borderRight: "11px solid rgba(255,255,255,0.97)",
            }}
          />
        )}

        <div className="rounded-2xl border border-slate-100 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.28)] p-6 flex flex-col gap-4">

          {/* Progress bar */}
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === step ? 22 : 6,
                  background: i === step ? "#1E5B4F" : "#E3E5E6",
                }}
              />
            ))}
            <span className="ml-auto text-[11px] text-slate-400 font-semibold tracking-wide">
              {step + 1} / {STEPS.length}
            </span>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-[17px] font-bold text-slate-900 mb-2">
              {current.title}
            </h3>
            <div className="max-h-52 overflow-y-auto pr-1 [scrollbar-width:thin] [scrollbar-color:#C49A1F_transparent] flex flex-col gap-2">
              {renderDescription(current.description)}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2 pt-1">
            {!isFirst && (
              <button
                onClick={prev}
                className="px-4 py-2 text-sm font-semibold rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-all duration-200"
              >
                ← Back
              </button>
            )}
            <button
              onClick={finish}
              className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors px-2"
            >
              Skip
            </button>
            <button
              onClick={next}
              className="ml-auto px-5 py-2 text-sm font-semibold rounded-xl border border-[#C49A1F]/65 bg-[#1E5B4F]/90 text-white shadow-[0_6px_20px_rgba(30,91,79,0.30)] hover:bg-[#1E5B4F] transition-all duration-200"
            >
              {isLast ? "Finish ✓" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
